import React, { useEffect, useState } from 'react';
import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import Image from '../../components/images/Image';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo, logout } from '../../utils';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import NotFound from '../../components/common/NotFound';
import useResetSubmittingOnRestore from '../../hooks/useResetSubmittingOnRestore';

const fallbackActivity = require('./../../assets/images/fallback/activity.png');

const Page = () => {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    fetchUserInfo('/battle-select', setUserInfo, setLoading, setError);
  }, []);

  // Keep the battle cards clickable after the browser restores this page from the
  // back/forward cache (otherwise the `submitting` lock stays stuck). See the hook.
  useResetSubmittingOnRestore(setSubmitting);

  const handleBattleSelect = async (battleId) => {
    if (submitting) return;
    try {
      setSubmitting(battleId);
      const response = await fetch('/api/battle-select', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          credentials: 'include',
        },
        body: JSON.stringify({ battle_id: battleId }),
      });
      if (!response.ok) throw new Error('Failed to select battle');
      await response.json();
      window.location.href = '/home';
    } catch (err) {
      console.error('Error selecting battle:', err);
      setSubmitting(null);
    }
  };

  const battles = userInfo?.battles || [];

  return (
    <Screen>
      <div className="top">
        <div className="event-select-shell">
          <div className="event-select-header">
            <Back to="/event-select" />
            <h2 className="event-select-header__title">{t('selectYourBattle')}</h2>
            <p className="event-select-header__sub">{t('selectYourBattleSub')}</p>
            <button
              className="event-select-header__logout"
              onClick={logout}
              aria-label={t('logout')}
            >
              <Icon name="sign-out-alt" theme="primary" />
            </button>
          </div>

          {battles.length > 0 ? (
            <div className="event-list">
              {battles.map((battle) => {
                const isSubmitting = submitting === battle.id;
                return (
                  <Card
                    key={battle.id}
                    mx="0"
                    mb="2"
                    className={`event-card ${isSubmitting ? 'event-card--loading' : ''}`}
                    onClick={() => handleBattleSelect(battle.id)}
                  >
                    <div className="event-card__row">
                      <Image
                        size="4.5rem"
                        className="event-card__image"
                        url={battle.photo_url ? battle.photo_url : fallbackActivity}
                      />
                      <div className="event-card__body">
                        <div className="event-card__name">{battle.name}</div>
                      </div>
                      <Icon name="angle-right" className="event-card__chevron" />
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <NotFound translationKey="noBattlesFound" loading={loading} />
          )}
        </div>
      </div>
    </Screen>
  );
};

export default Page;
